import { CONFIG, PLANETS, CHIME_AUDIO_PATH } from './config';
import { buildTargets, computeLayout, type SceneLayout } from './layout';
import {
  createDragOrTap,
  type DragOrTapController,
  type DraggableHandle,
} from './dragOrTapToPlace';
import { Narration } from './narration';
import type { PlanetId, Point } from './types';

/**
 * Activity orchestration: builds the scene, drives the prompt state machine,
 * and wires dragOrTapToPlace events to narration + completion.
 *
 * State machine (single active prompt — progressive disclosure):
 *   idle -> intro -> prompting(planet) -> [placed...] -> complete -> replay
 */

interface ActivityRefs {
  scene: HTMLElement;
  orbits: SVGSVGElement;
  silhouettes: HTMLElement;
  planetsLayer: HTMLElement;
  dock: HTMLElement;
  caption: HTMLElement;
  sun: HTMLImageElement;
  muteButton: HTMLButtonElement;
  replayInstructionButton: HTMLButtonElement;
  completionOverlay: HTMLElement;
  replayButton: HTMLButtonElement;
  muteIcon: HTMLImageElement;
}

interface PlacedInfo {
  id: PlanetId;
  centre: Point;
}

export class Activity {
  private layout!: SceneLayout;
  private controller!: DragOrTapController;
  private handles = new Map<PlanetId, DraggableHandle>();
  private placedOrder: PlacedInfo[] = [];
  private currentPromptIndex = -1;
  private hintTimer: number | null = null;
  private hintPulseTimer: number | null = null;
  private interactionListeners: Array<() => void> = [];
  private reducedMotion = false;
  /**
   * Monotonic round epoch. Incremented on every replay so async continuations
   * (onPlaced / complete) started in an earlier round can detect they are
   * stale and no-op instead of mutating the new round.
   */
  private round = 0;
  /** Guards complete() to run at most once per round. */
  private completionStarted = false;

  constructor(
    private refs: ActivityRefs,
    private narration: Narration,
  ) {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.attachHud();
  }

  async begin() {
    this.measureLayout();
    this.renderScene();
    this.buildDock(randomOrder());
    this.attachController();
    // PRP AC-02: intro narration plays immediately after the start tap.
    await this.narration.play('intro');
    this.advancePrompt();
  }

  private measureLayout() {
    this.layout = computeLayout(window.innerWidth, window.innerHeight);
  }

  private renderScene() {
    const { sunCentre, sunDiameter, slotCentres, orbitRadii } = this.layout;
    const sun = this.refs.sun;
    sun.style.width = `${sunDiameter}px`;
    sun.style.height = `${sunDiameter}px`;
    sun.style.left = `${sunCentre.x - sunDiameter / 2}px`;
    sun.style.top = `${sunCentre.y - sunDiameter / 2}px`;

    // Orbit arcs: a horizontal arc through each slot centre.
    const svg = this.refs.orbits;
    svg.setAttribute('viewBox', `0 0 ${this.layout.width} ${this.layout.height}`);
    svg.setAttribute('width', String(this.layout.width));
    svg.setAttribute('height', String(this.layout.height));
    svg.innerHTML = '';
    const ns = 'http://www.w3.org/2000/svg';
    for (const planet of PLANETS) {
      const r = orbitRadii[planet.id];
      const circle = document.createElementNS(ns, 'circle');
      circle.setAttribute('cx', String(sunCentre.x));
      circle.setAttribute('cy', String(sunCentre.y));
      circle.setAttribute('r', String(r));
      circle.setAttribute('class', 'orbit-arc');
      svg.appendChild(circle);
    }

    // Silhouettes: faded greyscale versions of the real art (same shape).
    const silhouettes = this.refs.silhouettes;
    silhouettes.innerHTML = '';
    for (const planet of PLANETS) {
      const centre = slotCentres[planet.id];
      const diameter = this.layout.slotDiameters[planet.id];
      const wrap = document.createElement('div');
      wrap.className = 'silhouette';
      wrap.dataset.planet = planet.id;
      wrap.style.width = `${diameter}px`;
      wrap.style.height = `${diameter}px`;
      wrap.style.left = `${centre.x - diameter / 2}px`;
      wrap.style.top = `${centre.y - diameter / 2}px`;
      const img = document.createElement('img');
      img.src = planet.artPath;
      img.alt = '';
      img.draggable = false;
      wrap.appendChild(img);
      silhouettes.appendChild(wrap);
    }
  }

  private buildDock(order: PlanetId[]) {
    const dock = this.refs.dock;
    dock.innerHTML = '';
    const layer = this.refs.planetsLayer;
    layer.innerHTML = '';
    const slotW = Math.min(160, window.innerWidth / (PLANETS.length + 1));
    order.forEach((id, _idx) => {
      const def = PLANETS.find((p) => p.id === id)!;
      const dockSlot = document.createElement('div');
      dockSlot.className = 'dock-slot';
      dockSlot.dataset.planet = id;
      dockSlot.style.width = `${slotW}px`;
      dockSlot.appendChild(document.createElement('div')).className = 'dock-rest-mark';

      // Wrapper = invisible hit area (>=96px per PRP AC-04); image is centred.
      const planetEl = document.createElement('div');
      planetEl.className = 'planet';
      planetEl.dataset.planet = id;
      planetEl.setAttribute('role', 'button');
      planetEl.setAttribute('aria-label', def.label);
      const img = document.createElement('img');
      img.src = def.artPath;
      img.alt = '';
      img.draggable = false;
      img.className = 'planet-art';
      // Clamp dock art to the slot so Jupiter/Saturn never spill into
      // neighbours or off-screen on narrow phones (the touch wrapper stays
      // >=96px regardless). NB below ~475px width the clamp flattens the
      // size ladder — matching is shape-based, so this trades the size cue
      // for non-overlap on the narrowest phones.
      const d = Math.min(def.baseDiameter, Math.round(slotW * 0.95));
      img.style.width = `${d}px`;
      img.style.height = `${d}px`;
      planetEl.appendChild(img);
      dockSlot.appendChild(planetEl);
      dock.appendChild(dockSlot);
    });
  }

  private homeFor(id: PlanetId): Point {
    const slot = this.refs.dock.querySelector<HTMLElement>(`.dock-slot[data-planet="${id}"]`);
    if (!slot) return { x: 0, y: 0 };
    const r = slot.getBoundingClientRect();
    const scene = this.refs.scene.getBoundingClientRect();
    return {
      x: r.left + r.width / 2 - scene.left,
      y: r.top + r.height / 2 - scene.top,
    };
  }

  private attachController() {
    const handles: DraggableHandle[] = PLANETS.map((p) => {
      const element = this.refs.planetsLayer.querySelector<HTMLElement>(`.planet[data-planet="${p.id}"]`)
        ?? this.refs.dock.querySelector<HTMLElement>(`.planet[data-planet="${p.id}"]`)!;
      // Precompute halfSize once so applyTransform avoids layout reads on the
      // pointermove hot path. PRP mandates a 96px minimum touch target.
      const halfSize = Math.max(96, element.offsetWidth) / 2;
      const handle: DraggableHandle = {
        id: p.id,
        element,
        home: this.homeFor(p.id),
        halfSize,
      };
      this.handles.set(p.id, handle);
      return handle;
    });
    // Move all planets into the planets-layer so they can be transformed freely.
    for (const h of handles) {
      if (h.element.parentElement !== this.refs.planetsLayer) {
        this.refs.planetsLayer.appendChild(h.element);
      }
      this.applyTransform(h.id, h.home, 'rest');
    }

    const targets = buildTargets(this.layout).map((t) => {
      const silhouette = this.refs.silhouettes.querySelector<HTMLElement>(
        `.silhouette[data-planet="${t.id}"]`,
      )!;
      const target = { id: t.id, target: t, element: silhouette };
      // Wire tap-tap: tapping the silhouette while a draggable is selected
      // attempts placement via the same validation path (PRP AC-07).
      silhouette.addEventListener('pointerdown', (_event) => {
        const rect = silhouette.getBoundingClientRect();
        const scene = this.refs.scene.getBoundingClientRect();
        const at: Point = {
          x: rect.left + rect.width / 2 - scene.left,
          y: rect.top + rect.height / 2 - scene.top,
        };
        this.controller.tapTarget(t.id, at);
        this.restartHintsForInteraction();
      });
      return target;
    });

    this.controller = createDragOrTap({
      draggables: handles,
      targets,
      scenePoint: (clientX, clientY) => {
        const r = this.refs.scene.getBoundingClientRect();
        return { x: clientX - r.left, y: clientY - r.top };
      },
      onMove: (id, point, mode) => this.applyTransform(id, point, mode),
      onPlaced: (draggableId, targetId, centre) => this.onPlaced(draggableId, targetId, centre),
      onReturned: (id) => this.onReturned(id),
      onSelectChanged: (id) => {
        this.restartHintsForInteraction();
        this.callMatchingHome(id);
      },
    });
  }

  private applyTransform(id: PlanetId, point: Point, mode: 'lifted' | 'selected' | 'rest') {
    const handle = this.handles.get(id);
    if (!handle) return;
    const el = handle.element;
    el.classList.toggle('is-lifted', mode === 'lifted' || mode === 'selected');
    el.classList.toggle('is-selected', mode === 'selected');
    // halfSize precomputed at attach; no layout read here (PRP §Performance).
    el.style.transform = `translate(${point.x - handle.halfSize}px, ${point.y - handle.halfSize}px)`;
  }

  private async onPlaced(id: PlanetId, _targetId: PlanetId, centre: Point) {
    const round = this.round;
    this.controller.markPlaced(id);
    this.placedOrder.push({ id, centre });
    // Decide synchronously at placement time, BEFORE any await: with the
    // serialized narration queue, resuming later would otherwise see all four
    // planets placed and every coroutine would take the complete() branch
    // (quadruple completion narration, skipped prompts, and — after a replay —
    // a stale complete() re-showing the overlay over the fresh round).
    const isFinal = this.placedOrder.length >= PLANETS.length;
    const el = this.handles.get(id)!.element;
    el.dataset.placed = 'true';
    await this.narration.playStandalone(CHIME_AUDIO_PATH);
    if (this.round !== round) return; // stale: replay happened mid-chime
    const def = PLANETS.find((p) => p.id === id)!;
    await this.narration.play(def.successNarrationId);
    if (this.round !== round) return; // stale: replay happened mid-line
    // Brief micro-animation flag.
    el.classList.add('is-celebrating');
    window.setTimeout(() => {
      if (this.round === round) el.classList.remove('is-celebrating');
    }, CONFIG.placeCelebrateMs);
    if (isFinal) {
      await this.complete(round);
    } else {
      this.advancePrompt();
    }
  }

  private onReturned(_planetId: PlanetId) {
    // Non-punitive: no audio, no visual negativity (PRP AC-06). State preserved.
  }

  private advancePrompt() {
    this.currentPromptIndex += 1;
    const next = PLANETS[this.currentPromptIndex];
    if (!next) return;
    void this.narration.play(next.promptNarrationId);
    this.startHintTimers(next.id);
  }

  private startHintTimers(planetId: PlanetId) {
    this.clearHintTimers();
    // Under reduced motion the planet still receives is-wiggle (CSS swaps it
    // for a gentle opacity pulse), but the silhouette scale pulse is skipped
    // entirely because scale is the kind of motion reduced-motion users opt
    // out of. PRP §Sensory & Accessibility.
    this.hintTimer = window.setTimeout(() => {
      const handle = this.handles.get(planetId);
      handle?.element.classList.add('is-wiggle');
      if (this.reducedMotion) return;
      this.hintPulseTimer = window.setTimeout(() => {
        const silhouette = this.refs.silhouettes.querySelector<HTMLElement>(
          `.silhouette[data-planet="${planetId}"]`,
        );
        silhouette?.classList.add('is-pulse');
      }, CONFIG.hintPulseExtraMs);
    }, CONFIG.hintWiggleMs);
  }

  private clearHintTimers() {
    if (this.hintTimer !== null) {
      clearTimeout(this.hintTimer);
      this.hintTimer = null;
    }
    if (this.hintPulseTimer !== null) {
      clearTimeout(this.hintPulseTimer);
      this.hintPulseTimer = null;
    }
    // Planets live in the planets-layer (not the dock) once attached.
    this.refs.scene.querySelectorAll('.planet.is-wiggle').forEach((el) => el.classList.remove('is-wiggle'));
    this.refs.silhouettes.querySelectorAll('.silhouette.is-pulse').forEach((el) =>
      el.classList.remove('is-pulse'),
    );
  }

  /**
   * When a planet is picked up (tap-tap select), its matching home answers
   * with a short opacity pulse: converts the game from memory to visible
   * matching for a pre-reader. Cleared on deselect/placement/replay.
   */
  private callMatchingHome(id: PlanetId | null) {
    this.refs.silhouettes
      .querySelectorAll('.silhouette.is-home-call')
      .forEach((el) => el.classList.remove('is-home-call'));
    if (!id) return;
    // Guard: only while the planet is still unplaced (post-placement the
    // selection is cleared anyway, but replay-time calls must no-op).
    const handle = this.handles.get(id);
    if (!handle || handle.element.dataset.placed === 'true') return;
    this.refs.silhouettes
      .querySelector(`.silhouette[data-planet="${id}"]`)
      ?.classList.add('is-home-call');
  }

  /**
   * PRP §Hints: "Timers reset on any interaction" — the countdown restarts
   * from the latest interaction for the currently prompted planet. Clears
   * timers first so repeated interactions never stack duplicate timers.
   * No-op when the prompted planet is already placed or the round is over.
   */
  private restartHintsForInteraction() {
    const current = PLANETS[this.currentPromptIndex];
    if (!current) {
      this.clearHintTimers();
      return;
    }
    const handle = this.handles.get(current.id);
    if (!handle || handle.element.dataset.placed === 'true') {
      this.clearHintTimers();
      return;
    }
    this.startHintTimers(current.id);
  }

  private async complete(round: number) {
    if (this.completionStarted) return; // at most once per round
    this.completionStarted = true;
    this.clearHintTimers();
    // Orbit animation: each placed planet traces its orbit briefly.
    for (const placed of this.placedOrder) {
      const handle = this.handles.get(placed.id);
      if (handle) handle.element.classList.add('is-orbiting');
    }
    window.setTimeout(() => {
      if (this.round !== round) return;
      for (const handle of this.handles.values()) handle.element.classList.remove('is-orbiting');
    }, CONFIG.orbitMs);
    await this.narration.play('completion');
    if (this.round !== round) return; // replay happened during the completion line
    this.refs.completionOverlay.hidden = false;
  }

  private attachHud() {
    this.refs.muteButton.addEventListener('click', () => {
      const next = !this.narration.muted_;
      this.narration.setMuted(next);
      try {
        localStorage.setItem(CONFIG.muteStorageKey, next ? '1' : '0');
      } catch {
        /* localStorage may be unavailable in private mode; non-fatal. */
      }
      this.refs.muteIcon.src = next ? '/art/icon-speaker-off.svg' : '/art/icon-speaker-on.svg';
    });
    this.refs.replayInstructionButton.addEventListener('click', () => {
      this.restartHintsForInteraction();
      const current = PLANETS[this.currentPromptIndex];
      if (current) void this.narration.play(current.promptNarrationId);
      else void this.narration.play('intro');
    });
    this.refs.replayButton.addEventListener('click', () => this.replay());
    for (const evt of ['pointerdown', 'pointermove', 'pointerup']) {
      const listener = () => this.restartHintsForInteraction();
      window.addEventListener(evt, listener);
      this.interactionListeners.push(() => window.removeEventListener(evt, listener));
    }
  }

  async replay() {
    // Invalidate every continuation (onPlaced/complete coroutines, hint
    // timers) still pending from the previous round, then reset round state.
    this.round += 1;
    this.completionStarted = false;
    this.clearHintTimers();
    this.refs.completionOverlay.hidden = true;
    this.placedOrder = [];
    this.currentPromptIndex = -1;
    // Dispose the previous controller's window listeners before reattaching,
    // otherwise every replay leaks three window-level listeners (PRP H2).
    this.controller.dispose();
    this.refs.dock.innerHTML = '';
    this.refs.planetsLayer.innerHTML = '';
    this.measureLayout();
    this.renderScene();
    this.buildDock(randomOrder());
    this.attachController();
    await this.narration.play('intro');
    this.advancePrompt();
  }

  suspend(): void {
    this.round += 1;
    this.clearHintTimers();
    this.refs.completionOverlay.hidden = true;
    this.narration.stop();
  }
}

function randomOrder(arr: PlanetId[] = PLANETS.map((p) => p.id)): PlanetId[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
